import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { fmtRM, fmtNum, fmtDate } from "../../lib/format";
import { useLang } from "../../lib/i18n";
import { StatCard, StatusBadge, Spinner } from "../../components/Shell";
import { Users, Wallet, FileCheck2, HandCoins, ArrowLeft, Eye, LogIn } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function TenantView() {
  const { tenantId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const { lang } = useLang();

  useEffect(() => {
    api.get(`/platform/tenants/${tenantId}/view`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || "Error"));
  }, [tenantId]);

  const impersonate = async () => {
    try {
      const r = await api.post("/platform/impersonate", { tenant_id: tenant.tenant_id });
      toast.success(`Login sebagai ${r.data.as}`);
      window.location.href = "/admin";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  if (error) return <p className="text-sm text-red-500">{error}</p>;
  if (!data) return <Spinner />;

  const { tenant, subscription, stats, monthly_collections, recent_payments, recent_members } = data;

  return (
    <div data-testid="platform-tenant-view">
      <Link to="/platform/tenants" data-testid="tenant-view-back" className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-deep hover:text-sky-700 transition-colors mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> {lang === "bm" ? "Kembali ke Senarai Penyewa" : "Back to Tenants"}
      </Link>

      <div className="bg-card rounded-xl border border-brand-deep/10 p-5 mb-6 flex flex-wrap items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-display font-bold text-lg" style={{ backgroundColor: tenant.branding?.primary_color || "#0284C7" }}>
          {(tenant.branding?.brand_name || tenant.name).slice(0, 1)}
        </div>
        <div className="flex-1 min-w-[220px]">
          <h1 className="font-display text-xl font-bold text-brand-dark flex items-center gap-2">
            {tenant.name}
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
              <Eye className="w-3 h-3" /> {lang === "bm" ? "Mod Lihat" : "View Only"}
            </span>
          </h1>
          <div className="text-xs text-muted-foreground mt-0.5">
            {tenant.slug}.kopera.app • {tenant.coop_type} • {lang === "bm" ? "Daftar" : "Joined"} {fmtDate(tenant.created_at)}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="impersonate-btn"
            onClick={impersonate}
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs font-bold px-4 py-2.5 rounded-xl hover:bg-slate-700 transition-colors"
          >
            <LogIn className="w-3.5 h-3.5" /> {lang === "bm" ? "Login Sebagai Admin" : "Login as Admin"}
          </button>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{lang === "bm" ? "Pelan" : "Plan"}</div>
            <div className="text-sm font-bold uppercase text-brand-deep">{tenant.plan} <span className="text-muted-foreground font-medium normal-case">({subscription?.cycle})</span></div>
          </div>
          <StatusBadge status={tenant.status} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard testid="view-stat-members" label={lang === "bm" ? "Ahli Aktif" : "Active Members"} value={`${fmtNum(stats.members_active)} / ${fmtNum(stats.members_total)}`} icon={Users} sub={`${stats.kyc_pending} KYC pending`} />
        <StatCard testid="view-stat-shares" label={lang === "bm" ? "Modal Saham" : "Share Capital"} value={fmtRM(stats.share_capital)} icon={Wallet} />
        <StatCard testid="view-stat-fees" label={lang === "bm" ? "Yuran Tertunggak" : "Fees Outstanding"} value={fmtRM(stats.fees_outstanding)} icon={HandCoins} />
        <StatCard testid="view-stat-approvals" label={lang === "bm" ? "Kelulusan Pending" : "Pending Approvals"} value={fmtNum(stats.pending_approvals)} icon={FileCheck2} />
      </div>

      <div className="grid lg:grid-cols-5 gap-6 mt-6">
        <div className="lg:col-span-3 bg-card rounded-xl border border-brand-deep/10 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-brand-dark">{lang === "bm" ? "Kutipan Bulanan" : "Monthly Collections"}</h3>
            <span className="text-xs font-semibold text-brand-deep bg-brand-light rounded-full px-3 py-1">
              {lang === "bm" ? "Bulan ini" : "This month"}: {fmtRM(stats.collected_this_month)}
            </span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly_collections}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0F172A10" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtRM(v)} />
                <Bar dataKey="total" fill="#0284C7" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card rounded-xl border border-brand-deep/10 p-5">
            <h3 className="font-display font-semibold text-brand-dark mb-4">{lang === "bm" ? "Bayaran Terkini" : "Recent Payments"}</h3>
            <div className="space-y-3">
              {recent_payments.map((p) => (
                <div key={p.payment_id} className="flex items-center justify-between gap-3 text-sm border-b border-brand-deep/5 pb-3 last:border-0">
                  <div className="min-w-0">
                    <div className="font-medium text-brand-dark truncate">{p.member_name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{p.receipt_no} • {p.method}</div>
                  </div>
                  <div className="font-mono font-semibold text-brand-deep shrink-0">{fmtRM(p.amount)}</div>
                </div>
              ))}
              {recent_payments.length === 0 && <p className="text-sm text-muted-foreground">Tiada bayaran lagi.</p>}
            </div>
          </div>
          <div className="bg-card rounded-xl border border-brand-deep/10 p-5">
            <h3 className="font-display font-semibold text-brand-dark mb-4">{lang === "bm" ? "Ahli Terkini" : "Recent Members"}</h3>
            <div className="space-y-3">
              {recent_members.map((m) => (
                <div key={m.member_id} className="flex items-center justify-between gap-3 text-sm border-b border-brand-deep/5 pb-3 last:border-0">
                  <div className="min-w-0">
                    <div className="font-medium text-brand-dark truncate">{m.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{m.member_no}</div>
                  </div>
                  <StatusBadge status={m.status} />
                </div>
              ))}
              {recent_members.length === 0 && <p className="text-sm text-muted-foreground">Tiada ahli lagi.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
